import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../../../app/store";
import { caseDocumentsAdapter } from "./caseDocumentsSlice";
import { DOCUMENT_CONFIG } from "../config/DocumentConfig";
import {
  DocumentType,
  DocumentEntityType,
} from "../../../../src/api/caseDocuments";
import { LEAType, LEATypeLabelMap } from "../../../../src/api/events";
import { getAllPartyPerpetrators } from "../../stakeholder/logic/stakeholderSlice";

const documentSelectors = caseDocumentsAdapter.getSelectors(
  (state: RootState) => state.caseDocuments.documents
);

export const selectAllDocuments = documentSelectors.selectAll;
export const selectDocumentById = documentSelectors.selectById;
export const selectDocumentIds = documentSelectors.selectIds;
export const selectDocumentsTotal = documentSelectors.selectTotal;

export const selectDocumentsLoading = (state: RootState) =>
  state.caseDocuments.loading;

export const selectDocumentsError = (state: RootState) =>
  state.caseDocuments.error;

export const selectDocumentsLastFetched = (state: RootState) =>
  state.caseDocuments.lastFetched;

export const selectUploadInProgress = (state: RootState) =>
  state.caseDocuments.uploading;

export const selectUploadError = (state: RootState) =>
  state.caseDocuments.uploadError;

export const selectIsDocumentDeleting =
  (documentId: string) => (state: RootState) =>
    state.caseDocuments.documents.entities[documentId]?.deleting ?? false;

export const selectDocumentDeleteError =
  (documentId: string) => (state: RootState) =>
    state.caseDocuments.documents.entities[documentId]?.deleteError ?? null;

export const selectIsDocumentReplacing =
  (documentId: string) => (state: RootState) =>
    state.caseDocuments.documents.entities[documentId]?.replacing ?? false;

export const selectDocumentReplaceError =
  (documentId: string) => (state: RootState) =>
    state.caseDocuments.documents.entities[documentId]?.replaceError ?? null;

export const selectIsDocumentDownloading =
  (documentId: string) => (state: RootState) =>
    state.caseDocuments.documents.entities[documentId]?.downloading ?? false;

export const selectDocumentDownloadError =
  (documentId: string) => (state: RootState) =>
    state.caseDocuments.documents.entities[documentId]?.downloadError ?? null;

// Sorted Document
export const selectSortedDocuments = createSelector(
  [selectAllDocuments],
  (documents) =>
    [...documents].sort((a, b) => {
      const orderA = DOCUMENT_CONFIG[a.document_type]?.order;
      const orderB = DOCUMENT_CONFIG[b.document_type]?.order;

      if (orderA !== orderB) return orderA - orderB;

      return (
        new Date(a.created_at).getTime() -
        new Date(b.created_at).getTime()
      );
    })
);

export const selectSortedDocumentsWithPartyName = createSelector(
  [selectSortedDocuments, getAllPartyPerpetrators],
  (documents, parties) => {
    const partyMap = new Map(
      parties.map((p) => [p.party_id, p.name])
    );

    return documents.map((doc) => ({
      ...doc,
      partyName:
        doc.entity_type === DocumentEntityType.PARTY_INVOLVED
          ? partyMap.get(doc.entity_id) ?? null
          : null,
      loanAccountId:
        doc.entity_type === DocumentEntityType.LOAN_ACCOUNT
          ? doc.entity_id
          : null,           
      leaName:
         doc.document_type === DocumentType.LEA_REPORT
          ? LEATypeLabelMap[doc.entity_id as LEAType] ?? doc.entity_id
          : null,        
    }));
  }
);

export const selectVisibleDocumentsByTypes = createSelector(
  [
    selectSortedDocuments,
    (
      _state: RootState,
      allowedTypes: ReadonlySet<DocumentType> | readonly DocumentType[]
    ) => allowedTypes,
  ],
  (documents, allowedTypes) => {
    const allowedSet =
      allowedTypes instanceof Set ? allowedTypes : new Set(allowedTypes);
    return documents.filter((doc) => allowedSet.has(doc.document_type));
  }
);

export const selectDocumentsByPartyId = (partyId: string) =>
  createSelector([selectAllDocuments], (documents) =>
    documents.filter(
      (doc) =>
        doc.entity_type === DocumentEntityType.PARTY_INVOLVED &&
        doc.entity_id === partyId
    )
  );

export const selectDocumentsByPartyIdAndType = (
  partyId: string,
  documentType: DocumentType
) =>
  createSelector([selectAllDocuments], (documents) =>
    documents.filter(
      (doc) =>
        doc.entity_type === DocumentEntityType.PARTY_INVOLVED &&
        doc.entity_id === partyId &&
        doc.document_type === documentType
    )
  );

export const selectDocumentsByType = (documentType: DocumentType) =>
  createSelector([selectAllDocuments], (documents) =>
    documents.filter(
      (doc) => doc.document_type === documentType
    )
  );

export const selectSCNForParty = (partyId: string) =>
  selectDocumentsByPartyIdAndType(partyId, DocumentType.SCN);

export const selectPartyDropdownOptions = createSelector(
  [getAllPartyPerpetrators],
  (parties) =>
    parties.map((party) => ({
      party_id: party.party_id,
      party_name: party.name,
    }))
);

export const selectDocumentsByTypeAndEntityId = (documentType: DocumentType, entityId: string) => 
   createSelector([selectAllDocuments], (documents) =>
    documents.filter(
      (doc) =>
        doc.document_type === documentType &&
        doc.entity_id === entityId
    ))